import React, { useRef, useState } from 'react';

type RovingRadioOptions = {
    count: number;
    /** Index of the checked radio, or -1 when none (mixed selection). */
    selectedIndex: number;
    onSelect: (index: number) => void;
    /**
     * Arrow keys check the radio they land on (native radio behavior). Turn off when choosing is expensive
     * or destructive: arrows then only move focus, and Enter / Space / click choose.
     */
    selectOnMove?: boolean;
};

const STEP: Record<string, number | 'first' | 'last'> = {
    ArrowRight: 1,
    ArrowDown: 1,
    ArrowLeft: -1,
    ArrowUp: -1,
    Home: 'first',
    End: 'last'
};

/**
 * Roving tabindex for a `role="radiogroup"` whose children carry `role="radio"`: one tab stop, arrows /
 * Home / End move (and check). Spread `groupProps` on the group and give each radio `tabIndex={index ===
 * tabbableIndex ? 0 : -1}`; call `pick(index)` from clicks so a focus-only group keeps its tab stop.
 */
export function useRovingRadio<E extends HTMLElement = HTMLDivElement>(options: RovingRadioOptions) {
    const { count, selectedIndex, onSelect, selectOnMove = true } = options;
    const groupRef = useRef<E>(null);
    const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

    const tabbableIndex =
        !selectOnMove && focusedIndex !== null && focusedIndex < count ? focusedIndex : Math.max(0, selectedIndex);

    function onKeyDown(event: React.KeyboardEvent<E>): void {
        const step = STEP[event.key];
        const radios = Array.from(groupRef.current?.querySelectorAll<HTMLElement>('[role="radio"]') ?? []);
        const current = radios.indexOf(event.target as HTMLElement);
        if (step === undefined || current === -1 || radios.length === 0) {
            return;
        }
        event.preventDefault();
        const next = step === 'first' ? 0 : step === 'last' ? radios.length - 1 : (current + step + count) % count;
        if (selectOnMove) {
            onSelect(next);
        } else {
            setFocusedIndex(next);
        }
        radios[next]?.focus();
    }

    function pick(index: number): void {
        setFocusedIndex(index);
        onSelect(index);
    }

    return { groupProps: { ref: groupRef, onKeyDown }, tabbableIndex, pick };
}
